/* AUTO-GENERATED from docs/*.pdf — do not edit by hand.
   Regenerate with scripts/parse_legal.py if the source PDFs change.
   Exception: `table` blocks are hand-authored where a source PDF table would
   otherwise flatten into unreadable text (e.g. the CCPA categories table). */

export type LegalBlock =
  | { t: "h2" | "p" | "li"; s: string }
  | { t: "table"; s?: string; headers: string[]; rows: string[][] };
export interface LegalDoc {
  id: string;
  title: string;
  org: string;
  lastUpdated: string;
  blocks: LegalBlock[];
}

export const LEGAL: Record<string, LegalDoc> = {
  "privacy": {
    "id": "privacy",
    "title": "Privacy Policy",
    "org": "Winter Desktops (Wintops)",
    "lastUpdated": "17 June 2026",
    "blocks": [
      {
        "t": "p",
        "s": "PLEASE READ THIS PRIVACY POLICY CAREFULLY."
      },
      {
        "t": "p",
        "s": "This Privacy Policy for Winter Desktops (\"we\", \"us\" or \"our\") describes how and why we might access, collect, store, use, and/or share (\"process\") your personal information when you use our services (\"Services\"), including when you:"
      },
      {
        "t": "li",
        "s": "Visit our website at www.winterdesktops.com/privacy or any website of ours that links to this Privacy Policy"
      },
      {
        "t": "li",
        "s": "Download and use our desktop application (Winter Panda) or any other application of ours that links to this Privacy Policy"
      },
      {
        "t": "li",
        "s": "Engage with us in other related ways, including any marketing or events"
      },
      {
        "t": "p",
        "s": "Questions or concerns? Reading this Privacy Policy will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at privacy@winterdesktops.com"
      },
      {
        "t": "h2",
        "s": "SUMMARY OF KEY POINTS"
      },
      {
        "t": "p",
        "s": "What personal information do we process? When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use."
      },
      {
        "t": "p",
        "s": "Do we process any sensitive personal information? Some of the information may be considered \"special\" or \"sensitive\" in certain jurisdictions, for example, your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information."
      },
      {
        "t": "p",
        "s": "Do we collect any information from third parties? We do not collect any information from third parties."
      },
      {
        "t": "p",
        "s": "How do we process your information? We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with the law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so."
      },
      {
        "t": "p",
        "s": "In what situations and with which types of parties do we share personal information? We may share information in special situations and with specific categories of third parties."
      },
      {
        "t": "p",
        "s": "How do we keep your information safe? We have adequate organisational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect access, steal, or modify your information."
      },
      {
        "t": "p",
        "s": "What are your rights? Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information."
      },
      {
        "t": "p",
        "s": "How do you exercise your rights? The easiest way to exercise your rights is by submitting a data subject access request, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws."
      },
      {
        "t": "h2",
        "s": "1. WHAT INFORMATION DO WE COLLECT?"
      },
      {
        "t": "p",
        "s": "Personal information you disclose to us"
      },
      {
        "t": "p",
        "s": "In Short: We collect personal information that you provide to us."
      },
      {
        "t": "p",
        "s": "We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us."
      },
      {
        "t": "p",
        "s": "Personal Information Provided by You. The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:"
      },
      {
        "t": "li",
        "s": "organisation names"
      },
      {
        "t": "li",
        "s": "contact person names"
      },
      {
        "t": "li",
        "s": "business phone numbers"
      },
      {
        "t": "li",
        "s": "email addresses"
      },
      {
        "t": "li",
        "s": "business addresses"
      },
      {
        "t": "li",
        "s": "usernames and passwords"
      },
      {
        "t": "li",
        "s": "contact or authentication data"
      },
      {
        "t": "li",
        "s": "billing information"
      },
      {
        "t": "p",
        "s": "Sensitive information. We do not process sensitive information."
      },
      {
        "t": "p",
        "s": "Payment Data. We may collect data necessary to process your payment if you choose to make purchases, such as your payment instrument number, and the security code associated with your payment instrument. All payment data is handled and stored by Stripe. You may find their privacy notice link(s) here: https://stripe.com/au/privacy or on their website. Payment information is processed directly by Stripe. Winter Desktops does not store full payment card details."
      },
      {
        "t": "p",
        "s": "We may use third-party service providers to host the platform, deliver emails, process payments and support platform operations."
      },
      {
        "t": "p",
        "s": "All personal information that you provide to us must be true, and accurate, and you must notify us of any changes to such personal information."
      },
      {
        "t": "p",
        "s": "Business customers may provide recipient email addresses for the purpose of distributing Winter Panda invitations. We use these email addresses solely to send invitations, track redemption status, manage licence allocation and operate the gifting platform."
      },
      {
        "t": "h2",
        "s": "2. HOW DO WE PROCESS YOUR INFORMATION?"
      },
      {
        "t": "p",
        "s": "In short, we process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with the law. We process the personal information for the following purposes listed below. We may also process your information for other purposes only with your prior explicit consent."
      },
      {
        "t": "p",
        "s": "We process your personal information for a variety of reasons, depending on how you interact with our Services, including:"
      },
      {
        "t": "li",
        "s": "To facilitate account creation and authentication and otherwise manage user accounts. We may process your information so you can create and log in to your account, as well as keep your account in working order."
      },
      {
        "t": "li",
        "s": "To save or protect an individual's vital interest. We may process your information when necessary to save or protect an individual's vital interest, such as to prevent harm."
      },
      {
        "t": "h2",
        "s": "3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?"
      },
      {
        "t": "p",
        "s": "In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfil our contractual obligations, to protect your rights, or to fulfil our legitimate business interests. In some circumstances, we may disclose de-identified information for approved research or statistics projects, subject to ethics oversight and confidentiality commitments."
      },
      {
        "t": "p",
        "s": "If you are located in the EU or UK, this section applies to you."
      },
      {
        "t": "p",
        "s": "The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases we rely on in order to process your personal information. As such, we may rely on the following legal bases to process your personal information:"
      },
      {
        "t": "li",
        "s": "Consent. We may process your information if you have given us permission (i.e., consent) to use your personal information for a specific purpose. You can withdraw your consent at any time."
      },
      {
        "t": "li",
        "s": "Legal Obligations. We may process your information where we believe it is necessary for compliance with our legal obligations, such as to cooperate with a law enforcement body or regulatory in which we are involved."
      },
      {
        "t": "li",
        "s": "Vital Interests. We may process your information where we believe it is necessary to protect your vital interests or the vital interests of a third party, such as situations involving potential threats to the safety of any person."
      },
      {
        "t": "p",
        "s": "If you are located in Canada, this section applies to you."
      },
      {
        "t": "p",
        "s": "We may process your information if you have given us specific permission (i.e., express consent) to use your personal information for a specific purpose, or in situations where your permission can be inferred (i.e., implied consent). You can withdraw your consent at any time."
      },
      {
        "t": "p",
        "s": "In some exceptional cases, we may be legally permitted under applicable law to process your information without your consent, including, for example:"
      },
      {
        "t": "li",
        "s": "If the collection is clearly in the interests of an individual and consent cannot be obtained in a timely way"
      },
      {
        "t": "li",
        "s": "For investigations, fraud detection, and prevention"
      },
      {
        "t": "li",
        "s": "If it is contained in a witness statement and the collection is necessary to assess,"
      },
      {
        "t": "p",
        "s": "process, or settle an insurance claim  For identifying injured, ill, or deceased persons and communicating with next of kin  If we have reasonable grounds to believe an individual has been, is or may be victim"
      },
      {
        "t": "p",
        "s": "of financial abuse  If it is reasonable to expect that collection and use with consent would compromise"
      },
      {
        "t": "p",
        "s": "the availability or the accuracy of the information, and the collection is reasonable for purposes related to investigating a breach of an agreement or a contravention of the laws of Canada or a province.  If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records.  If it was produced by an individual in the course of their employment, business, or profession, and the collection is consistent with the purposes for which the information was produced  If the collection is solely for journalistic, artistic, or literary purposes  If the information is publicly available and is specified by the regulations  We may disclose de-identified information for approved research or statistics projects, subject to ethics oversight and confidentiality commitments."
      },
      {
        "t": "h2",
        "s": "4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION"
      },
      {
        "t": "p",
        "s": "In Short: We may share information in specific situations described in this section and/or with the following categories of third parties."
      },
      {
        "t": "p",
        "s": "We may share your data with third-party vendors, service providers, consultants, contractors, or agents (\"third parties\") who perform services for us or on our behalf and require access to such information to do that work."
      },
      {
        "t": "p",
        "s": "We may share aggregated or de-identified information with research institutes, universities, colleges, charities and not-for-profit organisations for research, educational, product improvement or statistical purposes."
      },
      {
        "t": "p",
        "s": "We may also need to share your personal information in the event of a business transfer. We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company."
      },
      {
        "t": "h2",
        "s": "5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES"
      },
      {
        "t": "p",
        "s": "In Short: We may use cookies and other tracking technologies to collect and store your information."
      },
      {
        "t": "p",
        "s": "We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. Some online tracking technologies help us maintain the security of our Services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic site functions."
      },
      {
        "t": "p",
        "s": "We also permit third parties and service providers to use online tracking technologies on our Services for analytics and advertising, including to help manage and display advertisements, to tailor advertisements to your interests, or to send abandoned shopping cart reminders (depending on your communication preferences). Third parties and service providers use their technology to provide advertising for products and services tailored to your interests, which may appear on our Services or on other websites."
      },
      {
        "t": "p",
        "s": "To the extent these online tracking technologies are deemed to be a \"sale\"/\"sharing\" (which includes targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt out of these online tracking technologies by submitting a request."
      },
      {
        "t": "h2",
        "s": "6. HOW LONG DO WE KEEP YOUR INFORMATION?"
      },
      {
        "t": "p",
        "s": "In Short: We keep your information for as long as necessary to fulfil the purposes outlined in this Privacy Policy unless otherwise required by law."
      },
      {
        "t": "p",
        "s": "We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Policy, unless a longer retention period is required or permitted by law such as tax, accounting, or other legal requirements). No purpose in this notice will require us to keep your personal information for longer than 5 years after the termination of the user's account."
      },
      {
        "t": "p",
        "s": "When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymise such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible."
      },
      {
        "t": "h2",
        "s": "7. HOW DO WE KEEP YOUR INFORMATION SAFE?"
      },
      {
        "t": "p",
        "s": "In Short: We aim to protect your personal information through a system or organisational and technical security measures."
      },
      {
        "t": "p",
        "s": "We have implemented appropriate and reasonable technical and organisational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information to and from our Services. You assume your own risks and should access the Services only within a secure environment."
      },
      {
        "t": "h2",
        "s": "8. DO WE COLLECT INFORMATION FROM MINORS?"
      },
      {
        "t": "p",
        "s": "In Short: We do not knowingly collect data from or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction."
      },
      {
        "t": "p",
        "s": "We do not knowingly collect, solicit data from, or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or the equivalent age as specified by law in your jurisdiction or that you are the parent or guardian of such a minor and consent to such minor's use of the Services. If we learn that personal information from users under 18 years of age, or the equivalent age under applicable law in your jurisdiction, has been collected, we will deactivate the account and promptly take reasonable measures to delete such data from our records. If you become aware of any data we may have collected from children under age 18 or the equivalent age as specified by law in your jurisdiction, please contact us at privacy@winterdesktops.com"
      },
      {
        "t": "h2",
        "s": "9. WHAT ARE YOUR PRIVACY RIGHTS?"
      },
      {
        "t": "p",
        "s": "In Short: Depending on your state of residence in the US or in some regions, such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time, depending on your country, province, or state of residence."
      },
      {
        "t": "p",
        "s": "In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information; (ii) to request rectification or erasure; (iii) to restrict the processing of your personal information; (iv) if applicable, to data portability; and (v) not to be subject to automated decision-making. If a decision that produces legal or similarly significant effects is made solely by automated means, we will inform you, explain the main factors, and offer a simple way to request human review. In certain circumstances, you may also have the right to object to the processing of your personal information. You can make"
      },
      {
        "t": "p",
        "s": "such a request by contacting us privacy@winterdesktops.com We will consider and act upon any request in accordance with applicable data protection laws."
      },
      {
        "t": "p",
        "s": "If you are located in the EEA or UK and you believe we are unlawfully processing your personal information, you also have the right to complain to your Member State data protection authority or the UK data protection authority."
      },
      {
        "t": "p",
        "s": "If you are located in Switzerland, you may contact the Federal Data Protection and Information Commissioner."
      },
      {
        "t": "p",
        "s": "Withdrawing your consent: If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time by terminating your account."
      },
      {
        "t": "p",
        "s": "However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent."
      },
      {
        "t": "p",
        "s": "Account Information"
      },
      {
        "t": "p",
        "s": "If you would at any time like to review or change the information in your account or terminate your account, you can:"
      },
      {
        "t": "li",
        "s": "Log in to your account settings and update your user account."
      },
      {
        "t": "p",
        "s": "Upon your request to terminate your account, we will deactivate or delete your account and information from our active database. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements."
      },
      {
        "t": "p",
        "s": "Cookies and similar technologies: Most Web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your browser to remove cookies and to reject cookies. If you choose to remove cookies or reject cookies, this could affect certain features of our Services. If you have any questions or comments about your privacy rights, you may email us at privacy@winterdesktops.com"
      },
      {
        "t": "h2",
        "s": "10. CONTROLS FOR DO-NOT-TRACK FEATURES"
      },
      {
        "t": "p",
        "s": "Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track (\"DNT\") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognising and implementing DNT signals has been finalised. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will information you about that practice in a revised version of this Privacy Policy."
      },
      {
        "t": "p",
        "s": "California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognising or honouring DNT signals, we do not respond to them at this time."
      },
      {
        "t": "h2",
        "s": "11. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?"
      },
      {
        "t": "p",
        "s": "In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law."
      },
      {
        "t": "p",
        "s": "Categories of Personal Information We Collect"
      },
      {
        "t": "p",
        "s": "The table below shows the categories of personal information we have collected in the past twelve (12) months. The examples provided are illustrative only and are intended to explain the types of information covered by each category. They do not necessarily reflect the specific personal information collected by Winter Desktops."
      },
      {
        "t": "table",
        "headers": ["Category", "Examples", "Collected"],
        "rows": [
          ["A. Identifiers", "Personal details, such as legal name, alias, home and postal addresses, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name.", "YES"],
          ["B. Personal information as defined in the California Customer Records statute", "Employment details, and financial information.", "YES"],
          ["C. Protected classification characteristics under state or federal law", "Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data", "NO"],
          ["D. Commercial information", "Transaction information, purchase history, financial details, and payment information", "YES"],
          ["E. Biometric information", "Fingerprints and voiceprints", "NO"],
          ["F. Internet or other similar network activity", "Browsing history, search history, online behaviour, interest data, and interactions with our and other websites, applications, systems, and advertisements", "NO"],
          ["G. Geolocation data", "Device location", "NO"],
          ["H. Audio, electronic, sensory, or similar information", "Images and audio, video or call recordings created in connection with our business activities", "NO"],
          ["I. Professional or employment-related information", "Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us", "YES"],
          ["J. Education information", "Student records and directory information", "NO"],
          ["K. Inferences drawn from collected personal information", "Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics", "NO"],
          ["L. Sensitive personal information", "Any sensitive information", "NO"]
        ]
      },
      {
        "t": "p",
        "s": "We may also collect other personal information outside of these categories through instances where you interact with us in person, or by phone or mail in the context of:"
      },
      {
        "t": "li",
        "s": "Receiving help through our customer support channels;"
      },
      {
        "t": "li",
        "s": "Participation in customer surveys or contests; and"
      },
      {
        "t": "li",
        "s": "Facilitation in the delivery of our Services and to respond to your inquiries."
      },
      {
        "t": "p",
        "s": "How We Use and Share Personal Information"
      },
      {
        "t": "p",
        "s": "We may disclose your personal information to our service providers pursuant to a written contract between each service provider and us."
      },
      {
        "t": "p",
        "s": "We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be \"selling\" of your personal information."
      },
      {
        "t": "p",
        "s": "We may disclose aggregated or de-identified information for research, educational, product improvement, or statistical purposes. We do not disclose identifiable personal information to research organisations unless required by law or with your consent."
      },
      {
        "t": "p",
        "s": "Your Rights"
      },
      {
        "t": "p",
        "s": "You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:"
      },
      {
        "t": "li",
        "s": "Right to know whether or not we are processing your personal data"
      },
      {
        "t": "li",
        "s": "Right to access your personal data"
      },
      {
        "t": "li",
        "s": "Right to correct inaccuracies in your personal data"
      },
      {
        "t": "li",
        "s": "Right to request the deletion of your personal data"
      },
      {
        "t": "li",
        "s": "Right to obtain a copy of the personal data you previously shared with us"
      },
      {
        "t": "li",
        "s": "Right to non-discrimination for exercising your rights"
      },
      {
        "t": "li",
        "s": "Right to opt out of the processing of your personal data if it is used for targeted"
      },
      {
        "t": "p",
        "s": "advertising (or sharing as defined under California's privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects (\"profiling\")"
      },
      {
        "t": "p",
        "s": "How to Exercise Your Rights"
      },
      {
        "t": "p",
        "s": "To exercise these rights, you can submit a data subject access request by emailing us at privacy@winterdesktops.com, or by referring to the contact details at the bottom of this document."
      },
      {
        "t": "p",
        "s": "You can opt out of the selling of your personal information, targeted advertising, or profiling by disabling cookies in Cookie Preference Settings."
      },
      {
        "t": "p",
        "s": "Under certain US state data protection laws, you can designate an authorised agent to make a request on your behalf. We may deny a request from an authorised agent that does not provide proof that they have been validly authorised to act on your behalf in accordance with applicable laws."
      },
      {
        "t": "p",
        "s": "Request Verification"
      },
      {
        "t": "p",
        "s": "Upon receiving your request, we will need to verify your identity to determine that you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information to verify your identity and for security or fraud prevention."
      },
      {
        "t": "p",
        "s": "If you submit the request through an authorised agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf."
      },
      {
        "t": "p",
        "s": "Appeals"
      },
      {
        "t": "p",
        "s": "Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at privacy@winterdesktops.com. We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general."
      },
      {
        "t": "p",
        "s": "California \"Shine The Light\" Law"
      },
      {
        "t": "p",
        "s": "California Civil Code Section 1798.83. also known as the \"Shine The Light\" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us via email privacy@winterdesktops.com."
      },
      {
        "t": "h2",
        "s": "12. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?"
      },
      {
        "t": "p",
        "s": "In Short: You may have additional rights based on the country you reside in."
      },
      {
        "t": "p",
        "s": "Australia and New Zealand"
      },
      {
        "t": "p",
        "s": "We collect and process your personal information under the obligations and conditions set by Australia's Privacy Act 1988 and New Zealand's Privacy Act 2020 (Privacy Act)."
      },
      {
        "t": "p",
        "s": "This Privacy Notice satisfies the notice requirements defined in both Privacy Acts, in particular: what personal information we collect from you, from which sources, for which purposes, and other recipients of your personal information."
      },
      {
        "t": "p",
        "s": "If you do not wish to provide the personal information necessary to fulfil their applicable purpose, it may affect our ability to provide our services, in particular:"
      },
      {
        "t": "li",
        "s": "offer you the products or services that you want"
      },
      {
        "t": "li",
        "s": "respond to or help with your requests"
      },
      {
        "t": "li",
        "s": "manage your account with us"
      },
      {
        "t": "li",
        "s": "confirm your identity and protect your account"
      },
      {
        "t": "p",
        "s": "At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us via email privacy@winterdesktops.com."
      },
      {
        "t": "p",
        "s": "If you believe we are unlawfully processing your personal information, you have the right to submit a complaint about a breach of the Australian Privacy Principles to the Office of the Australian Information Commissioner and a breach of New Zealand's Privacy Principles to the Office of the New Zealand Privacy Commissioner."
      },
      {
        "t": "p",
        "s": "Republic of South Africa"
      },
      {
        "t": "p",
        "s": "At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us via email privacy@winterdesktops.com"
      },
      {
        "t": "p",
        "s": "If you are unsatisfied with the manner in which we address any compliant with regard to our processing of personal information, you can contact the Information Regulator (South Africa)."
      },
      {
        "t": "h2",
        "s": "13. DO WE MAKE UPDATES TO THIS PRIVACY POLICY?"
      },
      {
        "t": "p",
        "s": "In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws."
      },
      {
        "t": "p",
        "s": "We may update this Privacy Policy from time to time. The updated version will be indicated by an updated \"Revised\" date at the top of this Privacy Policy. If we make material changes to this Privacy Policy, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Policy frequently to be informed of how we are protecting your information."
      },
      {
        "t": "h2",
        "s": "14. HOW CAN YOU CONTACT US ABOUT THIS POLICY?"
      },
      {
        "t": "p",
        "s": "If you have any questions or comments about this notice, you may email us at privacy@winterdesktops.com"
      },
      {
        "t": "p",
        "s": "Winter Desktops C/- Lavina & Tommy Inc. 30 N Gould St Ste R Sheridan, WY 82801 United States"
      },
      {
        "t": "h2",
        "s": "15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?"
      },
      {
        "t": "p",
        "s": "Based on the applicable laws of your country or state of residence in the US, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please contact us via email at privacy@winterdesktops.com"
      }
    ]
  },
  "terms": {
    "id": "terms",
    "title": "Terms & Conditions",
    "org": "Winter Desktops (Wintops)",
    "lastUpdated": "19 June 2026",
    "blocks": [
      {
        "t": "h2",
        "s": "AGREEMENT TO OUR LEGAL TERMS"
      },
      {
        "t": "p",
        "s": "Winter Desktops (\"Company,\" \"we\", \"us\" or \"our\") operates Winter Panda application, as well as any other related products and services that refer or link to these legal terms (the \"Legal Terms\") (collectively, the \"Services\")"
      },
      {
        "t": "p",
        "s": "These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity (\"you\"), and us, concerning your access to and use of the Services. You agree that by accessing the Services, you have read, understood, and agreed to be bound by all of these Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY."
      },
      {
        "t": "p",
        "s": "Supplemental terms and conditions or documents that may be posted on the Services from time to time are hereby expressly incorporated herein by reference. We reserve the right, in our sole discretion, to make changes or modifications to these Legal Terms at any time and for any reason. We will alert you about any changes by updating the \"Last updated\" date of these Legal Terms, and you waive any right to receive specific notice of each such change. It is your responsibility to periodically review these Legal Terms to stay informed of updates. You will be subjected to, and will be deemed to have been made aware of and to have accepted, the changes in any revised Legal Terms by your continued use of the Services after the date such revised Legal Terms are posted."
      },
      {
        "t": "p",
        "s": "We recommend that you print a copy of these Legal Terms for your records."
      },
      {
        "t": "h2",
        "s": "1. OUR SERVICES"
      },
      {
        "t": "p",
        "s": "The information provided when using the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country. Accordingly, those persons who choose to access the Services from other locations do so on their own initiative and are solely responsible for compliance with local laws, if and to the extent local laws are applicable. Messages, prompts and wellness content are provided for informational and entertainment purposes only and are not medical, psychological or professional advice. We do not guarantee any specific wellbeing, productivity, educational or therapeutic outcome from use of the Services."
      },
      {
        "t": "h2",
        "s": "2. INTELLECTUAL PROPERTY RIGHTS"
      },
      {
        "t": "p",
        "s": "Our intellectual property"
      },
      {
        "t": "p",
        "s": "We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the \"Content\"), as well as the trademarks, service marks, and logos contained therein (the \"Marks\")."
      },
      {
        "t": "p",
        "s": "Our Content and Marks are protected by copyright and trademark laws (and various other intellectual property rights and unfair competition laws) and treaties around the world."
      },
      {
        "t": "p",
        "s": "The Content and Marks are provided in or through the Services \"AS IS\" for your personal, non-commercial use or internal business purpose only."
      },
      {
        "t": "p",
        "s": "Your use of our Services"
      },
      {
        "t": "p",
        "s": "Subject to your compliance with these Legal Terms, including the \"prohibited activities\" in the following sections, we grant you a non-exclusive, non-transferable, revocable license to:"
      },
      {
        "t": "li",
        "s": "Access the Services; and"
      },
      {
        "t": "li",
        "s": "Download or print a copy of any portion of the Content to which you have properly"
      },
      {
        "t": "p",
        "s": "gained access."
      },
      {
        "t": "p",
        "s": "solely for your personal, non-commercial use or internal business purpose."
      },
      {
        "t": "p",
        "s": "Except as set out in this section or elsewhere in our Legal Terms, no part of the Services and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission."
      },
      {
        "t": "p",
        "s": "If you wish to make any use of the Services, Content, or Marks other than as set out in this section or elsewhere in our Legal Terms, please address your request to privacy@winterdesktops.com. If we ever grant you the permission to post, reproduce, or publicly display any part of our Services or Content, you must identify us as the owners or licensors of the Services, Content, or Marks and ensure that any copyright or proprietary notice appears or is visible on posting, reproducing, or displaying our Content."
      },
      {
        "t": "p",
        "s": "We reserve all rights not expressly granted to you in and to the Services, Content and Marks."
      },
      {
        "t": "p",
        "s": "Any breach of these Intellectual Property Rights will constitute a material breach of our Legal Terms and your right to use our Services will terminate immediately."
      },
      {
        "t": "p",
        "s": "Your submissions"
      },
      {
        "t": "p",
        "s": "Please review this section and the \"prohibited activities\" section below carefully prior to using our Services to understand the (a) rights you gave us and (b) obligations you have when you post or upload any content through the Services."
      },
      {
        "t": "p",
        "s": "Submissions: By directly sending us any question, comment, suggestion, idea, feedback, or other information about the Services (\"Submissions\"), you agree to assign to us all intellectual property rights in such Submission. You agree that we shall own this Submission and be entitled to its unrestricted use and dissemination for any lawful purpose, commercial or otherwise, without acknowledgement or compensation to you."
      },
      {
        "t": "p",
        "s": "You are responsible for what you post or upload: By sending us Submissions through any part of the Services you:"
      },
      {
        "t": "li",
        "s": "confirm that you have read and agree with our \"prohibited activities\" and will not post, send, publish, upload or transmit through the Services any Submission that is illegal, harassing, hateful, harmful, defamatory, obscene, bullying, abusive, discriminatory, threatening to any person or group, sexually explicit, false, inaccurate, deceitful, or misleading;"
      },
      {
        "t": "li",
        "s": "to the extent permissible by applicable law, waive any and all moral rights to any such Submission;"
      },
      {
        "t": "li",
        "s": "warrant that any such Submission is original to you or that you have the necessary rights and licenses to submit such Submissions and that you have full authority to grant us the above-mentioned rights in relation to your Submissions;"
      },
      {
        "t": "li",
        "s": "warrant and represent that your Submissions do not constitute confidential information."
      },
      {
        "t": "p",
        "s": "You are solely responsible for your Submissions, and you expressly agree to reimburse us for any and all losses that we may suffer because of your breach of (a) this section, (b) any third party's intellectual property rights, or (c) applicable law."
      },
      {
        "t": "h2",
        "s": "3. USER REPRESENTATIONS"
      },
      {
        "t": "p",
        "s": "By using the Services, you represent and warrant that: (1) you have the legal capacity and you agree to comply with these Legal Terms; (2) you are not a minor in the jurisdiction in which you reside; (3) you will not access the Services through automated or non-human means, whether through a bot, script or otherwise; (4) you will not use the Services for any illegal or unauthorised purpose; and (5) your use of the Services will not violate any applicable law or regulation."
      },
      {
        "t": "p",
        "s": "If you provide any information that is untrue, inaccurate, not current, or incomplete, we have the right to suspend or terminate your account and refuse any and all current or future use of the Services (or any portion thereof)."
      },
      {
        "t": "h2",
        "s": "4. PROHIBITED ACTIVITIES"
      },
      {
        "t": "p",
        "s": "You may not access or use the Services for any purpose other than that for which we make the Services available. The Services may not be used in connection with any commercial endeavours except those that are specifically endorsed or approved by us."
      },
      {
        "t": "p",
        "s": "As a user of the Services, you agree not to:"
      },
      {
        "t": "li",
        "s": "Systematically retrieve data or other content from the Services to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us."
      },
      {
        "t": "li",
        "s": "Trick, defraud, or mislead other users and us, especially in any attempt to learn sensitive account information such as user passwords."
      },
      {
        "t": "li",
        "s": "Circumvent, disable, or otherwise interfere with security-related features of the Services, including features that prevent or restrict the use or copying of any Content or enforce limitations on the use of the Services and/or the Content contained therein."
      },
      {
        "t": "li",
        "s": "Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services."
      },
      {
        "t": "li",
        "s": "Use any information obtained from the Services in order to harass, abuse, or harm"
      },
      {
        "t": "li",
        "s": "Make improper use of our support services or submit a false report of abuse or misconduct."
      },
      {
        "t": "li",
        "s": "Use the Services in a manner inconsistent with any applicable laws or regulations."
      },
      {
        "t": "li",
        "s": "Engage in unauthorised framing of or linking to the Services."
      },
      {
        "t": "li",
        "s": "Upload or transmit (or attempt to upload or to transmit) viruses, Trojan horses, or other material, including excessive use of capital letters and spamming (continuous posting of repetitive text), that interferes with any party’s uninterrupted use and enjoyment of the Services or modifies, impairs, disrupts, alters, or interferes with the use, features, functions, operation, or maintenance of the Services. "
      },
      {
        "t": "li",
        "s": "Engage in any automated use of the system, such as using scripts to send comments or messages, or using any data mining, robots, or similar data gathering and extraction tools."
      },
      {
        "t": "li",
        "s": "Delete the copyright or other proprietary rights notice from any Content"
      },
      {
        "t": "li",
        "s": "Attempt to impersonate another user or person, or use the username of another user."
      },

      {
        "t": "li",
        "s": "Upload or transmit (or attempt to upload or to transmit) any material that acts as a passive or active information collection or transmission mechanism, including, without limitation, clear graphics interchange formats (“gifs”), 1x1 pixels, web bugs, cookies, or other similar devices (sometimes  eferred to as “spyware” or “passive collection mechanisms” or “pcms”)."
      },
      {
        "t": "li",
        "s": "Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services."
      },
      {
        "t": "li",
        "s": "Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Services to you."
      },
      {
        "t": "li",
        "s": "Attempt to bypass any measures of the Services designed to prevent or restrict access to the Services, or any portion of the Services."
      },
      {
        "t": "li",
        "s": "Copy or adapt the Services’ software, including but not limited to Flash, PHP, MTML, JavaScript, or other code."
      },
      {
        "t": "li",
        "s": "Except as permitted by applicable law, decipher, decompile, disassemble, or reverse engineer any of the software comprising or in any way making up a part of the Services."
      },

      {
        "t": "li",
        "s": "Except as may be the result of standard search engine or internet browser usage, use, launch, develop, or distribute any automated system, including without limitation, any spider, robot, cheat utility, scraper, or offline reader that accesses the Services, or use or launch any unauthorised script of other software."
      },
      {
        "t": "li",
        "s": "Use a buying agent or purchasing agent to make purchases on the Services."
      },
      {
        "t": "li",
        "s": "Make any unauthorised use of the Services, including collecting usernames and/or email addresses of users by electronic or other means for the purpose of sending unsolicited email, or creating user accounts by automated means or under false pretences."
      },
      {
        "t": "li",
        "s": "Use the Services as part of any effort to compete with us or otherwise use the Services and/or the Content for any revenue-generating endeavour or commercial enterprise."
      },
      {
        "t": "h2",
        "s": "5. USER-GENERATED CONTRIBUTIONS"
      },
      {
        "t": "p",
        "s": "The Services do not offer users the ability to submit or post content. We may provide you with the opportunity to create, submit, post, display, transmit, perform, publish, distribute, or broadcast content and materials to us or on the Services, including but not limited to text, writings, video, audio, photographs, graphics, comments, suggestions, or personal information or other material (collectively \"Contributions\"). Contributions may be viewable by other users of the Services and through third-party websites."
      },
      {
        "t": "h2",
        "s": "6. CONTRIBUTED LICENSE"
      },
      {
        "t": "p",
        "s": "You and Services agree that we may access, store, process, and use any information and personal data that you and your choices (including settings)."
      },
      {
        "t": "p",
        "s": "By submitting suggestions or other feedback regarding the Services, you agree that we can use and share such feedback for any purpose without compensation to you."
      },
      {
        "t": "p",
        "s": "We do not assert any ownership over your Contributions. You retain full ownership of all of your Contributions and any intellectual property rights or other proprietary rights associated with your Contributions. We are not liable for any statements or representations in your Contributions provided by you in any area on the Services. You are solely responsible for your Contributions to the Services, and you expressly agree to exonerate us from any and all responsibility and to refrain from any legal action against us regarding your Contributions."
      },
      {
        "t": "h2",
        "s": "7. SERVICES MANAGEMENT"
      },
      {
        "t": "p",
        "s": "We reserve the right, but not the obligation, to: (1) monitor the Services for violations of these Legal Terms; (2) take appropriate legal action against anyone who, in our sole discretion, violates the laws or these Legal Terms, including without limitation, reporting such user to law enforcement authorities; (3) in our sole discretion and without limitation, refuse, restrict access to, limit the availability of, or disable (to the extent technologically feasible) any of your Contributions or any portion thereof; (4) in our sole discretion and without limitation, notice, or liability, to remove from the Services or otherwise disable all files and content that are excessive in size or are in any way burdensome to our systems; and (5) otherwise manage the Services in a manner designed to protect our rights and property and to facilitate the proper functioning of the Services."
      },
      {
        "t": "h2",
        "s": "8. TERM AND TERMINATION"
      },
      {
        "t": "p",
        "s": "These Legal Terms shall remain in full force and effect while you use the Services. WITHOUT LIMITING ANY OTHER PROVISION OF THESE LEGAL TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE SERVICES (INCLUDING BLOCKING CERTAIN IP ADDRESSES), TO ANY PERSON FOR ANY REASON OR FOR NO REASON, INCLUDING WITHOUT LIMITATION FOR BREACH OF ANY REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN THESE LEGAL TERMS OR OF ANY APPLICABLE LAW OR REGULATION. WE MAY TERMINATE YOUR USE OR PARTICIPATION IN THE SERVICES OR DELETE ANY CONTENT OR INFORMATION THAT YOU POSTED AT ANY TIME, WITHOUT WARNING, IN OUR SOLE DISCRETION."
      },
      {
        "t": "p",
        "s": "If we terminate or suspend your account for any reason, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party, even if you may be acting on behalf of the third party. In addition to terminating or suspending your account, we reserve the right to take appropriate legal action, including, without limitation, pursuing civil, criminal, and injunctive redress."
      },
      {
        "t": "h2",
        "s": "9. MODIFICATIONS AND INTERRUPTIONS"
      },
      {
        "t": "p",
        "s": "We reserve the right to change, modify, or remove the contents of the Services at any time or for any reason at our sole discretion without notice. However, we have no obligation to update any information on our Services. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the Services."
      },
      {
        "t": "p",
        "s": "We cannot guarantee the Services will be available at all times. We may experience hardware, software, or other problems, or need to perform maintenance related to the Services, which may result in interruptions, delays, or errors. We reserve the right to change, revise, update, suspend, discontinue, or otherwise modify the Services at any time or for any reason without notice to you. You agree that we have no liability whatsoever for any loss, damage, or inconvenience caused by your inability to access or use the Services during any downtime or discontinuance of the Services. Nothing in these Legal Terms will be construed to obligate us to maintain and support the Services or to supply any corrections, updates, or releases in connection therewith."
      },
      {
        "t": "p",
        "s": "10 GOVERNING LAW"
      },
      {
        "t": "p",
        "s": "These Legal Terms shall be governed by and defined following the laws of Wyoming, United States, and you irrevocably consent that the courts of Wyoming shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these legal terms. Nothing in these Terms excludes, restricts or modifies any consumer guarantee, right or remedy which cannot lawfully be excluded under applicable consumer protection laws, including the Australian Consumer Law."
      },
      {
        "t": "p",
        "s": "11 DISPUTE RESOLUTION"
      },
      {
        "t": "p",
        "s": "Informal Negotiations"
      },
      {
        "t": "p",
        "s": "To expedite resolution and control the cost of any dispute, controversy, or claim related to these Legal Terms (each a \"Dispute\" and collectively, the \"Disputes\") brought by either you or us (individually, a \"Party\" and collectively, the \"Parties\"), the Parties agree to first attempt to negotiate any Dispute (except those Disputes expressly provided below) informally for at least 90 days before initiating arbitration. Such informal negotiations commence upon written notice from one Party to the other Party."
      },
      {
        "t": "p",
        "s": "Binding Arbitration"
      },
      {
        "t": "p",
        "s": "If the parties are unable to resolve the dispute through informal negotiation, the dispute shall be finally resolved by arbitration in accordance with the United Nations Commission on International Trade Law Arbitration Rules in force at the time of commencement of the arbitration. The number of arbitrators shall be one (1). The seat or legal place of arbitration shall be in Wyoming, United States. The language of the proceedings shall be English. The governing law of these Legal Terms shall be the substantive law of the United States."
      },
      {
        "t": "p",
        "s": "Restrictions"
      },
      {
        "t": "p",
        "s": "The Parties agree that any arbitration shall be limited to the Dispute between the Parties individually. To the full extent permitted by law, (1) no arbitration shall be joined with any other proceeding; (b) there is no right or authority for any Dispute to be arbitrated on a class-action basis or to utilise class action procedures; (c) there is no right or authority for any Dispute to be brought in a purported representative capacity on behalf of the general public or any other persons."
      },
      {
        "t": "p",
        "s": "Exceptions to Informal Negotiations and Arbitration"
      },
      {
        "t": "p",
        "s": "The Parties agree that the following Disputes are not subject to the above provisions concerning informal negotiations binding arbitration: (1) any Disputes seeking to enforce or protect, or concerning the validity of, any of the intellectual property rights of a Party; (b) any Dispute related to, or arising from, allegations of theft, privacy, invasion of privacy, or unauthorised use; and (c) any claim for injunctive relief. If this provision is found to be illegal or unenforceable, then neither Party will elect to arbitrate any Dispute falling within that portion of this provision found to be illegal or unenforceable, and such Dispute shall be decided by a court of competent jurisdiction within the courts listed for jurisdiction above, and the Parties agree to submit to the personal jurisdiction of that court."
      },
      {
        "t": "h2",
        "s": "12. CORRECTIONS"
      },
      {
        "t": "p",
        "s": "There may be information on the Services that contains typographical errors, inaccuracies, or omissions, including descriptions, pricing, availability, and various other information. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information on the Services at any time, without prior notice."
      },
      {
        "t": "h2",
        "s": "13. DISCLAIMER"
      },
      {
        "t": "p",
        "s": "The Services are provided on an as-is and as-available basis. You agree that your use of the Services will be at your sole risk. To the fullest extent permitted by law, we disclaim all"
      },
      {
        "t": "p",
        "s": "warranties, express or implied, in connection with the Services and your use thereof, including, without limitation, the implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We make no warranties or representations about the accuracy or completeness of the Services' content or the content of any websites or applications linked to the Services and we will assume no liability or responsibility for any (1) errors, mistakes, or inaccuracies of content and materials, (2) personal injury or property damage, of any nature whatsoever, resulting from your access to and use of the services, (3) any unauthorised access to or use of our secure servers and/or any and all personal information and/or financial information stored therein, (4) any interruption or cessation of transmission to or from the Services, (5) any bugs, viruses, trojan horses, or the like which may be transmitted to or through the Services by any third party, and/or (6) any errors or omissions in any content and materials or for any loss or damage of any kind incurred as a result of the use of any content posted, transmitted, or otherwise made available via the Services. We do not warrant, endorse, guarantee, or assume responsibility for any product or service advertised or offered by a third party through the services, any hyperlinked website, or any website or mobile application featured in any banner or other advertising, and we will not be a party to or in any way be responsible for monitoring any transaction between you and any third-party providers of products or services. As with the purchase of a product or service through any medium or in any environment, you should use your best judgment and exercise caution where appropriate."
      },
      {
        "t": "h2",
        "s": "14. LIMITATIONS OF LIABILITY"
      },
      {
        "t": "p",
        "s": "In no event will we or our directors, employees or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the Services. Even if we have been advised of the possibility of such damages."
      },
      {
        "t": "p",
        "s": "Notwithstanding anything to the contrary contained herein, our liability to you for any cause whatsoever and regardless of the form of the action, will at all times be limited to the lesser of the amount paid in the preceding 12 months or USD$100, if any, by you to us. Certain US State Laws and International Laws do not allow limitations on implied warranties or the exclusion or limitation of certain damages. If these laws apply to you, some or all of the above disclaimers or limitations may not apply to you, and you may have additional rights."
      },
      {
        "t": "h2",
        "s": "15. INDEMNIFICATION"
      },
      {
        "t": "p",
        "s": "You agree to defend, indemnify, and hold us harmless, including our subsidiaries, affiliates, and all of our respective officers, agents, partners, and employees, from and against any loss, damage, liability, claim, or demand, including reasonable attorneys' fees and expenses, made by any third party due to or arising out of (1) use of the Services; (2) breach of these Legal Terms; (3) any breach of your representations and warranties set forth in these Legal Terms; (4) your violation of the rights of a third party, including but not limited to intellectual property rights; or (5) any overt harmful act toward any other user of the Services with whom you connected via the Services. Notwithstanding the foregoing, we reserve the right, at your expense, to assume the exclusive defence and control of any matter for which you are required to indemnify us, and you agree to cooperate, at your"
      },
      {
        "t": "p",
        "s": "expense, with our defence of such claims. We will use reasonable efforts to notify you of any such claim, action, or proceeding that is subject to this indemnification upon becoming aware of it."
      },
      {
        "t": "h2",
        "s": "16. USER DATA"
      },
      {
        "t": "p",
        "s": "We will retain certain data you transmit to the Services to manage the performance of the Services, as well as data relating to your use of the Services. Although we perform routine data backups, you are solely responsible for all data you transmit or that relates to any activity you undertake using the Services. You agree that we shall have no liability to you for any loss or corruption of any such data, and you hereby waive any right or action against us arising from any such loss or corruption of such data. We may use aggregated and anonymised usage data for product improvement, statistical analysis and research purposes."
      },
      {
        "t": "h2",
        "s": "17. ELECTRONIC COMMUNICATIONS, TRANSACTIONS AND SIGNATURES"
      },
      {
        "t": "p",
        "s": "Visiting the Services, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, and other communications we provide to you electronically, via email and on the Services, satisfy any legal requirement that such communication be in writing. YOU HEREBY AGREE TO THE USE OF ELECTRONIC SIGNATURES, CONTRACTS, ORDERS, AND OTHER RECORDS, AND TO ELECTRONIC DELIVERY OF NOTICES, POLICIES, AND RECORDS OF TRANSACTIONS INITIATED OR COMPLETED BY US OR VIA THE SERVICES. You hereby waive any rights or requirements under any statutes, regulations, rules, ordinances, or other laws in any jurisdiction which require an original signature or delivery or retention of non-electronic records, or to payments or the granting of credits by any means other than electronic means."
      },
      {
        "t": "h2",
        "s": "18. MISCELLANEOUS"
      },
      {
        "t": "p",
        "s": "These Legal Terms and any policies or operating rules posted by us on the Services or in respect of the Services constitute the entire agreement and understanding between you and us. Our failure to exercise or enforce any right or provision of these Legal Terms shall not operate as a waiver of such right or provision. These Legal Terms operate to the fullest extent permissible by law. We may assign any or all of our rights and obligations to others at any time. We shall not be responsible or liable for any loss, damage, delay, or failure to act caused by any cause beyond our reasonable control. If any provision or part of a provision of these Legal Terms is determined to be unlawful, void, or unenforceable, that provision or part of the provision is deemed severable from these Legal Terms and does not affect the validity and enforceability of any remaining provisions. There is no joint venture, partnership, employment or agency relationship created between you and us as a result of these Legal Terms or use of the Services. You agree that these Legal Terms will not be construed against us by virtue of having drafted them. You hereby waive any and all defences you may have based on the electronic form of these Legal Terms and the lack of signing by the parties hereto to execute these Legal Terms."
      },
      {
        "t": "h2",
        "s": "19. CONTACT US"
      },
      {
        "t": "p",
        "s": "In order to resolve a complaint regarding the Services or to receive further information regarding the use of the Services, please contact us at:"
      },
      {
        "t": "p",
        "s": "Winter Desktops C/- Lavina & Tommy Inc. 30 N Gould St Ste R Sheridan WY 82801 United States privacy@winterdesktops.com"
      }
    ]
  },
  "eula": {
    "id": "eula",
    "title": "End-User Licence Agreement",
    "org": "Winter Desktops (Wintops)",
    "lastUpdated": "17 June 2026",
    "blocks": [
      {
        "t": "p",
        "s": "PLEASE READ THIS END-USER LICENCE AGREEMENT CAREFULLY."
      },
      {
        "t": "p",
        "s": "This End User Licence Agreement (\"EULA\") is a legal agreement between Winter Desktops (\"we\", \"us\" or \"our\") and you (\"you\" or \"User\"). This EULA governs your download, installation, access to, and use of the Winter Panda desktop companion application, including its software, artwork, animations, text, messages, updates, patches, upgrades, features, content, installer, website-connected features, and related services (collectively, \"Winter Panda\", \"WPANDA\", or the \"Product\")."
      },
      {
        "t": "p",
        "s": "By downloading, installing, accessing, or using the Product, you agree to be bound by this EULA and our Privacy Policy, which is incorporated into this EULA by reference. If you do not agree, do not install or use the Product."
      },
      {
        "t": "h2",
        "s": "1. Eligibility and Acceptance"
      },
      {
        "t": "p",
        "s": "1.1 You must have legal capacity to enter into this EULA in your place of residence. If you are under the age required to enter binding contracts in your jurisdiction, your parent or legal guardian must review and accept this EULA on your behalf."
      },
      {
        "t": "p",
        "s": "1.2 If you access the Product through a third-party platform, including Steam, Epic, Microsoft Store, Apple, Google, or another distributor or marketplace, your use may also be subject to that platform's terms and conditions. If there is a conflict between this EULA and a platform's mandatory terms, the mandatory platform terms will prevail only to the extent of the conflict."
      },
      {
        "t": "h2",
        "s": "2. Licence Grant"
      },
      {
        "t": "p",
        "s": "2.1 Subject to your compliance with this EULA, we grant you a limited, revocable, nonexclusive, non-transferable, non-sublicensable licence to download, install, and use one copy of the Product for your personal, non-commercial use on compatible devices that you own or control."
      },
      {
        "t": "p",
        "s": "2.2 The Product is licensed, not sold. Except for the limited licence expressly granted under this EULA, we and our Intellectual Property (IP) licensors retain all rights, title, and interest in and to the Product."
      },
      {
        "t": "p",
        "s": "2.3 You may make a reasonable backup copy of the Product only where permitted by applicable law and only for archival or reinstallation purposes."
      },
      {
        "t": "h2",
        "s": "3. Restrictions on Use"
      },
      {
        "t": "p",
        "s": "You must not, and must not permit any other person to:"
      },
      {
        "t": "p",
        "s": "3.1 copy, reproduce, distribute, publicly display, publicly perform, publish, transmit, sell, rent, lease, lend, sublicense, assign, or otherwise exploit the Product except as expressly permitted by this EULA or applicable law;"
      },
      {
        "t": "p",
        "s": "3.2 reverse engineer, decompile, disassemble, adapt, translate, modify, or create derivative works of the Product, except to the limited extent such restriction is prohibited by applicable law and cannot lawfully be excluded;"
      },
      {
        "t": "p",
        "s": "3.3 remove, alter, obscure, or interfere with any copyright, trademark, proprietary notices, technical protections, or security features in the Product;"
      },
      {
        "t": "p",
        "s": "3.4 use the Product in any unlawful manner, or in any way that interferes with, disrupts, damages, or compromises the Product, our systems, or another person's use of the Product;"
      },
      {
        "t": "p",
        "s": "3.5 use bots, scripts, automation, packet manipulation, unauthorised launchers, code injection, memory editing, unauthorised overlays, or similar tools to alter or interfere with the intended operation of the Product;"
      },
      {
        "t": "p",
        "s": "3.6 use the Product for commercial exploitation without our prior written consent, including reselling access, offering the Product as part of a bureau, hosted, rental, or service arrangement, or using the Product in a way that generates direct commercial revenue from the software itself other than as expressly authorised by us;"
      },
      {
        "t": "p",
        "s": "3.7 circumvent any access controls, payment controls, activation systems, licence checks, regional restrictions, seat limitations, redemption code conditions, or other technical limitations;"
      },
      {
        "t": "p",
        "s": "3.8 use the Product in connection with any infringing, defamatory, abusive, deceptive, or otherwise unlawful content or activity."
      },
      {
        "t": "h2",
        "s": "4. Accounts, Activation Codes, and Business Access"
      },
      {
        "t": "p",
        "s": "4.1 Some features of the Product may require an account, licence key, redemption code, organisation code, or other access credentials. You are responsible for maintaining the confidentiality of your login details and for activities occurring under your credentials, except to the extent caused by our breach."
      },
      {
        "t": "p",
        "s": "4.2 If we offer employer-funded, school-funded, promotional, enterprise, or bulk access arrangements, any such access is still limited to authorised end users only. Access codes, invitation codes, organisation codes, or similar credentials may not be shared outside the permitted user group."
      },
      {
        "t": "p",
        "s": "4.3 We may suspend, revoke, or limit any code, credential, or licence where we reasonably suspect misuse, fraud, unauthorised sharing, security risks, breach of this EULA, or nonpayment under an applicable business arrangement."
      },
      {
        "t": "p",
        "s": "4.4 Unless expressly stated otherwise in a separate written agreement, a business, employer, school, or sponsor paying for access does not receive ownership of user accounts, user messages, user behavioural summaries, or personal profile data belonging to individual end users."
      },
      {
        "t": "p",
        "s": "4.5 Where access to the Product is provided by an employer, organisation, school, sponsor, or other third party, such party is responsible for determining its own compliance with applicable employment, privacy, workplace, education, or regulatory obligations. Winter Desktops does not monitor, supervise, or control how an organisation distributes access to the Product."
      },
      {
        "t": "h2",
        "s": "5. Product Features and Behavioural Inputs"
      },
      {
        "t": "p",
        "s": "5.1 Winter Panda is a desktop companion application. Depending on the version and your settings, the Product may respond to local device activity signals such as general input activity levels, idle time, interaction frequency, or basic system state in order to trigger animations, states, or display messages."
      },
      {
        "t": "p",
        "s": "5.2 The Product does not access, read, record, or store the content of your typing, files, communications, documents, or applications."
      },
      {
        "t": "p",
        "s": "5.3 Unless otherwise clearly disclosed, such activity signals are processed locally on your device and are not transmitted externally for analysis or profiling."
      },
      {
        "t": "p",
        "s": "5.4 The Product does not perform mood detection, emotional analysis, behavioural profiling, or behavioural optimisation. It is not designed or intended to provide psychological profiling, mental health assessment, or behavioural evaluation. It does not infer your mental state, intentions, or personal characteristics."
      },
      {
        "t": "p",
        "s": "5.5 The Product is designed as a lightweight ambient companion experience only. It is not intended to characterise users, influence behaviour, or provide structured guidance."
      },
      {
        "t": "p",
        "s": "5.6 The Product is not medical, psychological, psychiatric, therapeutic, or diagnostic software, and does not provide health care, counselling, or professional advice of any kind."
      },
      {
        "t": "p",
        "s": "5.7 Any messages, prompts, or reflections generated by the Product are general, nonpersonalised, and for entertainment or casual reflection purposes only, and must not be relied upon for decision-making or wellbeing support."
      },
      {
        "t": "p",
        "s": "5.8 You remain solely responsible for your wellbeing, decisions, and use of your device. If you require support, you should seek assistance from a qualified professional or appropriate services."
      },
      {
        "t": "h2",
        "s": "6. Privacy and Data"
      },
      {
        "t": "p",
        "s": "6.1 Our collection, use, storage, and disclosure of information are governed by our Privacy Policy."
      },
      {
        "t": "p",
        "s": "6.1A Data Minimisation and Local Processing The Product is designed to operate primarily using local processing on your device. The Product does not access, read, record, or store the content of your personal files, communications, documents, or typed input."
      },
      {
        "t": "p",
        "s": "Except where explicitly stated (for example, for basic functionality such as installation, updates, licence validation, or error diagnostics), we do not collect or retain personal data about your use of the Product."
      },
      {
        "t": "p",
        "s": "6.2 Depending on the Product version and your settings, the Product may process limited local device signals required to operate animations, trigger states, or display messages. Unless clearly disclosed otherwise, such signals are processed locally and are not transmitted for profiling or analysis."
      },
      {
        "t": "p",
        "s": "6.3 We do not claim ownership of your personal files, communications, or content stored outside the Product."
      },
      {
        "t": "p",
        "s": "6.4 You acknowledge that no software or transmission system can be guaranteed completely secure. To the maximum extent permitted by law, we do not guarantee that the Product will be free from unauthorised access, loss, corruption, delay, or interruption."
      },
      {
        "t": "h2",
        "s": "7. Updates, Patches, and Changes"
      },
      {
        "t": "p",
        "s": "7.1 We may provide patches, updates, upgrades, fixes, new features, balancing adjustments, content changes, or technical modifications to the Product."
      },
      {
        "t": "p",
        "s": "7.2 Some updates may be installed automatically, particularly where necessary for security, stability, compatibility, or continued operation of the Product."
      },
      {
        "t": "p",
        "s": "7.3 We may modify, suspend, discontinue, or remove any feature, content, functionality, online component, or support for older versions of the Product at any time, acting reasonably and subject to applicable law."
      },
      {
        "t": "h2",
        "s": "8. Intellectual Property"
      },
      {
        "t": "p",
        "s": "8.1 The Product, including its code, design, characters, animations, visual assets, text, dialogue, sound, music, logos, trademarks, artwork, interface, and all related intellectual property rights, is owned by us and/or our licensors and is protected by applicable intellectual property laws."
      },
      {
        "t": "p",
        "s": "8.2 \"Winter Panda\", \"WPANDA\", associated logos, artwork, and related brand features are intellectual property owned or used by us and/or our licensors and may be protected under applicable intellectual property laws."
      },
      {
        "t": "p",
        "s": "8.3 You must not use our name, logo, branding, screenshots, or assets for commercial purposes, misleading purposes, or in a way that suggests endorsement, sponsorship, partnership, or affiliation without our prior written consent."
      },
      {
        "t": "h2",
        "s": "9. User Content and Feedback"
      },
      {
        "t": "p",
        "s": "9.1 If the Product allows you to submit content, suggestions, bug reports, ideas, reviews, or other feedback, you warrant that you have the right to submit it and that it does not infringe any third-party rights or violate any law."
      },
      {
        "t": "p",
        "s": "9.2 To the extent permitted by law, you grant us a worldwide, non-exclusive, royalty-free, sublicensable licence to use, host, copy, modify, adapt, reproduce, publish, and otherwise exploit such feedback or submitted content for the purpose of operating, improving, promoting, and developing the Product."
      },
      {
        "t": "p",
        "s": "9.3 We are not obliged to keep any feedback confidential, compensate you for feedback, or use any feedback."
      },
      {
        "t": "h2",
        "s": "10. Third-Party Software and Services"
      },
      {
        "t": "p",
        "s": "10.1 The Product may include, bundle, interact with, or rely on third-party software, libraries, runtimes, engines, storefronts, APIs, payment processors, hosting providers, analytics providers, crash reporting tools, or operating system components."
      },
      {
        "t": "p",
        "s": "10.2 Your use of third-party components may be subject to separate licence terms, privacy terms, or notices from the relevant third party. To the extent required, those third-party terms are incorporated by reference or made available with the Product."
      },
      {
        "t": "p",
        "s": "10.3 We are not responsible for third-party software, services, websites, or content except to the extent required by applicable law."
      },
      {
        "t": "h2",
        "s": "11. Disclaimer of Warranties"
      },
      {
        "t": "p",
        "s": "11.1 To the maximum extent permitted by law, the Product is provided on an \"as is\" and \"as available\" basis."
      },
      {
        "t": "p",
        "s": "11.2 We do not warrant that the Product will be uninterrupted, error-free, secure, available at all times, compatible with every device or software configuration, or that defects will be corrected."
      },
      {
        "t": "p",
        "s": "11.3 We do not warrant that the Product will meet your expectations, achieve any intended emotional, productivity, or well-being outcome, or remain available in any particular form."
      },
      {
        "t": "p",
        "s": "11.4 Nothing in this EULA excludes, restricts, or modifies any consumer guarantee, statutory warranty, or other right that cannot lawfully be excluded, restricted, or modified under applicable law."
      },
      {
        "t": "h2",
        "s": "12. Consumer Rights and Refunds"
      },
      {
        "t": "p",
        "s": "12.1 This Product is a digital software product. Except where required by applicable law, a full refund will be provided within 14 days if no invitations have been sent."
      },
      {
        "t": "p",
        "s": "12.2 If you acquire the Product through a third-party platform (including Steam), refunds, cancellations, and chargebacks are subject to that platform's policies, which may apply in addition to this EULA."
      },
      {
        "t": "p",
        "s": "12.3 Nothing in this EULA excludes, restricts, or limits any rights you may have under mandatory consumer protection laws in your place of residence. Where such laws apply, they may provide you with rights to a refund or other remedies that cannot be excluded."
      },
      {
        "t": "p",
        "s": "12.4 To the maximum extent permitted by law, we are not required to provide refunds or compensation for:"
      },
      {
        "t": "p",
        "s": "(a) change of mind; (b) subjective dissatisfaction with the Product; (c) differences between your expectations and the Product's features or behaviour; or (d) failure to achieve any intended personal, emotional, or productivity outcome."
      },
      {
        "t": "p",
        "s": "12.5 To the extent permitted by applicable law, by downloading, installing, or using the Product, you acknowledge that performance of the Product begins immediately and that any applicable withdrawal or cooling-off rights may be reduced or waived once use has commenced."
      },
      {
        "t": "h2",
        "s": "13. Limitation of Liability"
      },
      {
        "t": "p",
        "s": "13.1 To the maximum extent permitted by law, and subject to section 12, we and our affiliates, licensors, contractors, distributors, and service providers are not liable for any indirect, incidental, special, exemplary, punitive, or consequential loss, or for any loss of profits, revenue, goodwill, data, business opportunity, or anticipated savings arising out of or in connection with the Product or this EULA."
      },
      {
        "t": "p",
        "s": "13.2 To the maximum extent permitted by law, and subject to section 12, our aggregate liability to you for all claims arising out of or in connection with the Product or this EULA will not exceed the greater of:"
      },
      {
        "t": "p",
        "s": "(a) the total amount you paid us for the Product in the 12 months before the event giving rise to the claim; and"
      },
      {
        "t": "p",
        "s": "(b) USD $100."
      },
      {
        "t": "p",
        "s": "13.3 Nothing in this EULA limits liability for fraud, fraudulent misrepresentation, death or personal injury caused by negligence where such liability cannot lawfully be excluded, or any other liability that cannot lawfully be excluded or limited."
      },
      {
        "t": "h2",
        "s": "14. Indemnity"
      },
      {
        "t": "p",
        "s": "14.1 To the maximum extent permitted by law, you agree to indemnify and hold harmless us and our affiliates, officers, employees, contractors, licensors, and service providers from and against claims, liabilities, damages, losses, and expenses (including reasonable legal costs) arising from:"
      },
      {
        "t": "p",
        "s": "(a) your breach of this EULA; (b) your misuse of the Product;"
      },
      {
        "t": "p",
        "s": "(c) your violation of any law or the rights of a third party; or"
      },
      {
        "t": "p",
        "s": "(d) content or material you submit through the Product, if any."
      },
      {
        "t": "p",
        "s": "14.2 This clause does not apply to the extent the claim, loss, or damage was caused by our negligence, wilful misconduct, or breach of law."
      },
      {
        "t": "h2",
        "s": "15. Term and Termination"
      },
      {
        "t": "p",
        "s": "15.1 This EULA starts when you first download, install, access, or use the Product and continues until terminated."
      },
      {
        "t": "p",
        "s": "15.2 You may terminate this EULA at any time by uninstalling the Product and ceasing all use."
      },
      {
        "t": "p",
        "s": "15.3 We may suspend or terminate your access to the Product, or terminate this EULA, if: (a) you materially breach this EULA;"
      },
      {
        "t": "p",
        "s": "(b) we reasonably suspect fraud, abuse, unauthorised sharing, unlawful conduct, or security risks;"
      },
      {
        "t": "p",
        "s": "(c) we are required to do so by law; or"
      },
      {
        "t": "p",
        "s": "(d) we discontinue the Product."
      },
      {
        "t": "p",
        "s": "15.4 On termination, the licence granted to you ends immediately and you must cease using the Product and delete or destroy all copies in your possession or control, except to the extent retention is required by law."
      },
      {
        "t": "p",
        "s": "15.5 Sections intended by their nature to survive termination, including ownership, disclaimers, limitations of liability, indemnity, governing law, and dispute resolution, survive termination."
      },
      {
        "t": "h2",
        "s": "16. Changes to This EULA"
      },
      {
        "t": "p",
        "s": "16.1 We may amend this EULA from time to time to reflect Product changes, legal requirements, security needs, or operational updates."
      },
      {
        "t": "p",
        "s": "16.2 If we make material changes, we will take reasonable steps to notify you, such as by posting an updated version in the Product, on our website, through the relevant storefront, or by other reasonable means."
      },
      {
        "t": "p",
        "s": "16.3 By continuing to use the Product after an updated EULA takes effect, you agree to the revised EULA to the extent permitted by law. If you do not agree, you must stop using the Product."
      },
      {
        "t": "h2",
        "s": "17. Governing Law and Disputes"
      },
      {
        "t": "p",
        "s": "17.1 This EULA is governed by the laws of the State of Wyoming, United States, without regard to conflict of laws principles."
      },
      {
        "t": "p",
        "s": "17.2 You and we agree that the courts located in Wyoming, United States, have jurisdiction over disputes arising out of or in connection with this EULA."
      },
      {
        "t": "p",
        "s": "17.3 However, nothing in this EULA excludes, restricts, or limits any rights you may have under mandatory consumer protection laws in your place of residence."
      },
      {
        "t": "p",
        "s": "17.4 Before commencing formal legal proceedings, the parties will use reasonable efforts to resolve the dispute through good faith discussions."
      },
      {
        "t": "h2",
        "s": "18. Arbitration and Class Action Waiver"
      },
      {
        "t": "p",
        "s": "18.1 Informal Resolution Before initiating any formal dispute, you and we agree to attempt to resolve the dispute informally by providing written notice and engaging in good faith discussions for at least thirty (30) days."
      },
      {
        "t": "p",
        "s": "18.2 Binding Arbitration To the maximum extent permitted by applicable law, any dispute, claim, or controversy arising out of or relating to this EULA or the Product (including non-contractual claims) shall be resolved exclusively through binding arbitration, rather than in court, except as otherwise provided in this Section."
      },
      {
        "t": "p",
        "s": "18.3 Arbitration Procedure Arbitration shall be administered by a recognised alternative dispute resolution provider mutually agreed by the parties. The arbitration may be conducted:"
      },
      {
        "t": "p",
        "s": "(a) in person; (b) by telephone or video conference; or (c) based solely on written submissions;"
      },
      {
        "t": "p",
        "s": "at the election of the party initiating the arbitration, unless otherwise required by applicable law."
      },
      {
        "t": "p",
        "s": "The arbitrator shall have authority to award any remedies available under applicable law on an individual basis."
      },
      {
        "t": "p",
        "s": "18.4 Individual Claims Only (Class Action Waiver) You and we agree that all claims must be brought in an individual capacity only, and not as a plaintiff or class member in any purported class, consolidated, representative, or collective proceeding."
      },
      {
        "t": "p",
        "s": "You expressly waive any right to:"
      },
      {
        "t": "p",
        "s": "(a) participate in a class action; (b) participate in a class arbitration; (c) act as a private attorney general; (d) bring claims in a representative capacity."
      },
      {
        "t": "p",
        "s": "18.5 Small Claims Exception Notwithstanding the foregoing, either party may bring an individual claim in a small claims court where jurisdiction and claim thresholds permit."
      },
      {
        "t": "p",
        "s": "18.6 Costs and Fees Each party shall bear its own legal costs and expenses in connection with arbitration, except where:"
      },
      {
        "t": "p",
        "s": "(a) applicable law requires otherwise; or (b) the arbitrator determines otherwise."
      },
      {
        "t": "p",
        "s": "18.7 Enforceability If any provision of this section is found to be invalid or unenforceable, that provision shall be severed, and the remaining provisions shall continue in full force to the maximum extent permitted by law."
      },
      {
        "t": "h2",
        "s": "19. General"
      },
      {
        "t": "p",
        "s": "19.1 If any provision of this EULA is found unenforceable, the remaining provisions continue in full force to the extent permitted by law."
      },
      {
        "t": "p",
        "s": "19.2 Our failure to enforce any provision is not a waiver of that provision or any other provision. 19.3 You may not assign, transfer, or novate this EULA without our prior written consent. We may assign or transfer this EULA as part of a restructure, sale, merger, or transfer of the Product or related business. 19.4 This EULA, together with any applicable Privacy Policy and any express written commercial terms we provide to you, constitutes the entire agreement between you and us regarding the Product."
      },
      {
        "t": "h2",
        "s": "20. Contact Details"
      },
      {
        "t": "p",
        "s": "If you have questions about this EULA, please contact: Winter Desktops C/- Lavina & Tommy Inc. 30 Gould St Ste R Sheridan WY 82801 United States support@winterdesktops.com"
      }
    ]
  }
};
